import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getContentByVersionPath } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedBrandPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateByPath } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import LinksState from "@insite/client-framework/Store/Links/LinksState";
import translate from "@insite/client-framework/Translate";
import VmiPageTypes from "@insite/client-framework/Types/VmiPageTypes";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Breadcrumbs, { BreadcrumbsPresentationProps } from "@insite/mobius/Breadcrumbs";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LinkProps } from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import React, { FC, useRef } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { pathname } = getLocation(state);
    return {
        links: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
        currentPage: getCurrentPage(state).type,
        pathname,
    };
};

export interface PageBreadcrumbsStyles {
    breadcrumbs?: BreadcrumbsPresentationProps;
}

interface OwnProps {
    extendedStyles?: PageBreadcrumbsStyles;
}

export const pageBreadcrumbStyles: PageBreadcrumbsStyles = {
    breadcrumbs: {
        css: css`
            width: 100%;
            display: flex;
            a {
                span {
                    &:first-letter {
                        text-transform: capitalize;
                    }
                }
            }
            .disable-breadcrumb {
                pointer-events: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            padding-bottom: 10px;
                            overflow-x: auto;
                            white-space: nowrap;
                        `,
                    ],
                    "max",
                )}

            span[aria-current="page"] {
                color: #2450a8;
                font-weight: 600;
            }

            /* Mobile Breadcrumb for all pages */
            &.mobile-breadcrumb {
                width: calc(100% - 160px);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                // display: flex;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 100%;
                                a {
                                    display: none;
                                    &:last-of-type {
                                        display: inline-flex;
                                    }

                                    &:before {
                                        content: "<";
                                        color: #2450a8;
                                        padding-right: 10px;
                                        font-size: 18px;
                                    }
                                }

                                > span {
                                    display: none;
                                }
                            `,
                            null,
                            css`
                                padding-bottom: 10px;
                                overflow-x: auto;
                                white-space: nowrap;
                                text-overflow: inherit;
                            `,
                        ],
                        "max",
                    )}
                span[aria-current="page"] {
                    color: #2450a8;
                    font-weight: 600;
                }
            }
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const homePageLink = { children: translate("Home"), href: "/" };

const PageBreadcrumbs: FC<Props> = (props: Props) => {
    const { links: linksValue, linksState, nodeId, extendedStyles, pathname } = props;
    const getBrandFromPathname = (pathname: string) => {
        const segments = pathname.split("/").filter(Boolean);

        if (segments[0] === "brands" && segments.length >= 2) {
            return segments[1];
        }

        return null;
    };

    const formatBrandName = (slug: string) => slug.replace(/-/g, " ").replace(/\b\w/g, char => char.toUpperCase());

    const cleanBrandName = (brandName: string) => brandName.replace(/[™®©℠]/g, "").trim();

    let links = linksValue || generateLinksFrom(linksState, nodeId);

    const brandSlug = getBrandFromPathname(pathname);

    if (brandSlug && links.length > 0) {
        // Find the actual brand breadcrumb so its casing is authoritative.
        const brandLink = links.find(link => typeof link.href === "string" && link.href === `/brands/${brandSlug}`);

        const brandName =
            typeof brandLink?.children === "string" ? cleanBrandName(brandLink.children) : formatBrandName(brandSlug);

        const brandIndex = brandLink ? links.indexOf(brandLink) : -1;

        const brandPrefixRegex = new RegExp(`^${brandName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+`, "i");

        links = links.map((link, index) => {
            // Clean the brand breadcrumb itself.
            if (index === brandIndex && typeof link.children === "string") {
                return {
                    ...link,
                    children: cleanBrandName(link.children),
                };
            }

            // Apply the brand to every category breadcrumb after the brand.
            if (brandIndex !== -1 && index > brandIndex && typeof link.children === "string") {
                if (link.children.toLowerCase() === "shop all products") {
                    return link;
                }

                const categoryName = link.children.replace(brandPrefixRegex, "").trim();

                return {
                    ...link,
                    children: `${brandName} ${categoryName}`,
                };
            }

            return link;
        });
    }

    const mounted = useRef<boolean>(false);
    if (links.length > 0 && links[0].href !== homePageLink.href) {
        links = [homePageLink, ...links];
    }

    React.useEffect(() => {
        if (typeof window !== "undefined") {
            const selectStateEl: HTMLSelectElement | null = document.querySelector(
                '[data-test-selector="pageBreadcrumbs"]',
            );
            if (selectStateEl) {
                selectStateEl.classList.add("mobile-breadcrumb");
            }
            // Do componentDidMount logic
            if (!mounted.current) {
                window.onpopstate = () => {
                    window.location.reload();
                };
                mounted.current = true;
                disableAndAddBreadcrumbFunction();
            } else {
                // Do componentDidUpdate logic
                disableAndAddBreadcrumbFunction();
            }
        }
    });

    // Disabled Service, Expertise and Resources from breadcrum on change of navigation and add class
    const disableAndAddBreadcrumbFunction = () => {
        const disableBreadcrumb: HTMLSelectElement | null = document.querySelector(
            '[data-test-selector="pageBreadcrumbs"] a:nth-of-type(2)',
        );
        const selectStateEl: HTMLSelectElement | null = document.querySelector(
            '[data-test-selector="pageBreadcrumbs"]',
        );
        if (disableBreadcrumb) {
            if (
                disableBreadcrumb.textContent === "Services" ||
                disableBreadcrumb.textContent === "Expertise" ||
                disableBreadcrumb.textContent === "Resources"
            ) {
                disableBreadcrumb.classList.add("disable-breadcrumb");
            } else {
                disableBreadcrumb.classList.remove("disable-breadcrumb");
            }
        }
    };
    const styles = useMergeStyles("pageBreadcrumbs", pageBreadcrumbStyles, extendedStyles);

    return <Breadcrumbs links={links} data-test-selector="pageBreadcrumbs" {...styles.breadcrumbs} />;
};

export function generateLinksFrom(linksState: LinksState, nodeId: string) {
    const links: LinkProps[] = [];
    let currentLink = getPageLinkByNodeId({ links: linksState }, nodeId);
    const vmiExcludePageTypes: string[] = ["HomePage", "MyAccountPage"];
    const search = (targetText: string) => (sourceTextArray: string) => sourceTextArray === targetText;
    const isVmiPageType = !currentLink?.type ? false : VmiPageTypes.some(search(currentLink.type));
    while (currentLink) {
        const isVmiExcludePageType = !currentLink?.type ? false : vmiExcludePageTypes.some(search(currentLink.type));
        if (!isVmiPageType || !isVmiExcludePageType) {
            links.unshift({
                children: currentLink.type === "HomePage" ? translate("Home_Breadcrumb") : currentLink.title,
                href: currentLink.url,
                type: currentLink.type,
            });
        }
        currentLink = currentLink.parentId
            ? getPageLinkByNodeId({ links: linksState }, currentLink.parentId)
            : undefined;
    }

    return links;
}

export default connect(mapStateToProps)(PageBreadcrumbs);
